<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store.js';
import { useRoute } from 'vue-router';

import ConversationApi from 'dashboard/api/inbox/conversation';
import KanbanColumn from './KanbanColumn.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const STORAGE_VERSION = 2;
const storageKey = computed(() => `kanban-board-v2-${route.params.accountId}`);

const inboxes = computed(() => {
  const all = store.getters['inboxes/getAllInboxes'] || [];
  return Object.fromEntries(all.map(i => [i.id, i]));
});
const inboxList = computed(() => store.getters['inboxes/getAllInboxes'] || []);
const labelList = computed(() => store.getters['labels/getLabels'] || []);
const agentList = computed(() => store.getters['agents/getAgents'] || []);

// Default colors palette
const DEFAULT_COLORS = ['#10b981', '#f59e0b', '#64748b', '#3b82f6', '#a855f7', '#ec4899', '#ef4444'];
const randomColor = () =>
  DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)];
const newId = () => `col_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const columns = ref([]);
// conversationId -> columnId
const assignments = reactive({});
// conversationId -> conversation object cache
const conversationCache = reactive({});

const persist = () => {
  const data = {
    version: STORAGE_VERSION,
    columns: columns.value,
    assignments: { ...assignments },
  };
  localStorage.setItem(storageKey.value, JSON.stringify(data));
};

const loadState = () => {
  try {
    const raw = localStorage.getItem(storageKey.value);
    if (!raw) return false;
    const data = JSON.parse(raw);
    columns.value = data.columns || [];
    Object.keys(assignments).forEach(k => delete assignments[k]);
    Object.assign(assignments, data.assignments || {});
    return columns.value.length > 0;
  } catch {
    return false;
  }
};

const seedDefaultColumns = () => {
  columns.value = [
    { id: newId(), title: t('KANBAN.COLUMNS.OPEN'), color: '#10b981', seedStatus: 'open' },
    { id: newId(), title: t('KANBAN.COLUMNS.PENDING'), color: '#f59e0b', seedStatus: 'pending' },
    { id: newId(), title: t('KANBAN.COLUMNS.RESOLVED'), color: '#64748b', seedStatus: 'resolved' },
  ];
};

// Fetch conversations for a given status (used for seeding + add panel)
const fetchByStatus = async (status, page = 1) => {
  const {
    data: { data: { payload = [] } = {} },
  } = await ConversationApi.get({ page, status, assigneeType: 'all' });
  return payload;
};

const seedAssignmentsFromBackend = async () => {
  const statuses = ['open', 'pending', 'resolved'];
  const pages = await Promise.all(statuses.map(s => fetchByStatus(s, 1)));
  statuses.forEach((status, idx) => {
    const col = columns.value.find(c => c.seedStatus === status);
    if (!col) return;
    pages[idx].forEach(conv => {
      conversationCache[conv.id] = conv;
      if (!assignments[conv.id]) assignments[conv.id] = col.id;
    });
  });
  persist();
};

const refreshCachedConversations = async () => {
  // Re-fetch latest data for known conversations across all statuses
  const statuses = ['open', 'pending', 'resolved'];
  const pages = await Promise.all(statuses.map(s => fetchByStatus(s, 1)));
  pages.flat().forEach(conv => {
    conversationCache[conv.id] = conv;
  });
};

// Filters
const search = ref('');
const filterAgent = ref('');
const filterInbox = ref('');
const filterLabel = ref('');

const clearFilters = () => {
  search.value = '';
  filterAgent.value = '';
  filterInbox.value = '';
  filterLabel.value = '';
};

const matchesFilters = conv => {
  if (!conv) return false;
  if (search.value) {
    const q = search.value.toLowerCase();
    const name = (conv?.meta?.sender?.name || '').toLowerCase();
    const id = String(conv.display_id || '');
    if (!name.includes(q) && !id.includes(q)) return false;
  }
  if (filterAgent.value) {
    if (String(conv?.meta?.assignee?.id || '') !== String(filterAgent.value)) return false;
  }
  if (filterInbox.value) {
    if (String(conv.inbox_id) !== String(filterInbox.value)) return false;
  }
  if (filterLabel.value) {
    const labels = conv.labels || [];
    if (!labels.includes(filterLabel.value)) return false;
  }
  return true;
};

const conversationsByColumn = computed(() => {
  const map = {};
  columns.value.forEach(c => {
    map[c.id] = [];
  });
  Object.entries(assignments).forEach(([convId, colId]) => {
    const conv = conversationCache[convId];
    if (!conv || !map[colId]) return;
    if (!matchesFilters(conv)) return;
    map[colId].push(conv);
  });
  // Sort by timestamp desc
  Object.values(map).forEach(list =>
    list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  );
  return map;
});

const isLoading = ref(true);

// Column ops
const addColumnOpen = ref(false);
const newColumnTitle = ref('');
const newColumnColor = ref(DEFAULT_COLORS[0]);

const createColumn = () => {
  const title = newColumnTitle.value.trim();
  if (!title) return;
  columns.value.push({ id: newId(), title, color: newColumnColor.value });
  newColumnTitle.value = '';
  newColumnColor.value = randomColor();
  addColumnOpen.value = false;
  persist();
};

const renameColumn = ({ id, title }) => {
  const col = columns.value.find(c => c.id === id);
  if (col) {
    col.title = title;
    persist();
  }
};

const removeColumn = id => {
  columns.value = columns.value.filter(c => c.id !== id);
  Object.keys(assignments).forEach(convId => {
    if (assignments[convId] === id) delete assignments[convId];
  });
  persist();
};

const reorderColumn = ({ fromId, toId }) => {
  const fromIdx = columns.value.findIndex(c => c.id === fromId);
  const toIdx = columns.value.findIndex(c => c.id === toId);
  if (fromIdx === -1 || toIdx === -1) return;
  const [moved] = columns.value.splice(fromIdx, 1);
  columns.value.splice(toIdx, 0, moved);
  persist();
};

const onDropCard = ({ conversationId, toColumnId }) => {
  assignments[conversationId] = toColumnId;
  persist();
};

// Add conversation panel
const addPanelOpen = ref(false);
const addPanelColumnId = ref(null);
const addPanelSearch = ref('');
const addPanelLoading = ref(false);
const addPanelResults = ref([]);

const openAddPanel = async columnId => {
  addPanelColumnId.value = columnId;
  addPanelOpen.value = true;
  addPanelSearch.value = '';
  addPanelLoading.value = true;
  try {
    const statuses = ['open', 'pending', 'resolved'];
    const results = await Promise.all(statuses.map(s => fetchByStatus(s, 1)));
    const flat = results.flat();
    flat.forEach(conv => {
      conversationCache[conv.id] = conv;
    });
    addPanelResults.value = flat;
  } finally {
    addPanelLoading.value = false;
  }
};

const closeAddPanel = () => {
  addPanelOpen.value = false;
  addPanelColumnId.value = null;
  addPanelResults.value = [];
};

const filteredAddPanelResults = computed(() => {
  const q = addPanelSearch.value.toLowerCase().trim();
  return addPanelResults.value.filter(conv => {
    if (!q) return true;
    const name = (conv?.meta?.sender?.name || '').toLowerCase();
    const id = String(conv.display_id || '');
    return name.includes(q) || id.includes(q);
  });
});

const addConversationToColumn = conv => {
  if (!addPanelColumnId.value) return;
  conversationCache[conv.id] = conv;
  assignments[conv.id] = addPanelColumnId.value;
  persist();
  closeAddPanel();
};

// Export / import
const exportBoard = () => {
  const data = {
    version: STORAGE_VERSION,
    columns: columns.value,
    assignments: { ...assignments },
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `kanban-board-${route.params.accountId}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

const importInputRef = ref(null);
const triggerImport = () => importInputRef.value?.click();
const onImportFile = async e => {
  const file = e.target.files?.[0];
  if (!file) return;
  try {
    const text = await file.text();
    const data = JSON.parse(text);
    if (Array.isArray(data.columns)) {
      columns.value = data.columns;
      Object.keys(assignments).forEach(k => delete assignments[k]);
      Object.assign(assignments, data.assignments || {});
      persist();
      await refreshCachedConversations();
    }
  } catch {
    // ignore invalid file
  } finally {
    e.target.value = '';
  }
};

watch(columns, persist, { deep: true });

onMounted(async () => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('agents/get');

  const hasState = loadState();
  if (!hasState) {
    seedDefaultColumns();
    await seedAssignmentsFromBackend();
  } else {
    await refreshCachedConversations();
  }
  isLoading.value = false;
});
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-6 py-3 border-b border-n-weak flex-shrink-0 bg-n-solid-1"
    >
      <h1 class="text-lg font-semibold text-n-slate-12 mr-4">{{ t('KANBAN.TITLE') }}</h1>

      <!-- Filters -->
      <div class="flex items-center gap-2 flex-1 flex-wrap">
        <div class="relative">
          <Icon
            icon="i-lucide-search"
            class="absolute left-2.5 top-1/2 -translate-y-1/2 text-n-slate-9 size-4"
          />
          <input
            v-model="search"
            type="text"
            :placeholder="t('KANBAN.FILTERS.SEARCH_PLACEHOLDER')"
            class="pl-8 pr-3 py-1.5 text-sm bg-n-alpha-2 text-n-slate-12 rounded-lg border border-n-weak focus:outline-none focus:border-n-brand w-56"
          />
        </div>
        <select
          v-model="filterAgent"
          class="px-2 py-1.5 text-sm bg-n-alpha-2 text-n-slate-12 rounded-lg border border-n-weak focus:outline-none focus:border-n-brand"
        >
          <option value="">{{ t('KANBAN.FILTERS.ALL_AGENTS') }}</option>
          <option v-for="a in agentList" :key="a.id" :value="a.id">{{ a.name }}</option>
        </select>
        <select
          v-model="filterInbox"
          class="px-2 py-1.5 text-sm bg-n-alpha-2 text-n-slate-12 rounded-lg border border-n-weak focus:outline-none focus:border-n-brand"
        >
          <option value="">{{ t('KANBAN.FILTERS.ALL_INBOXES') }}</option>
          <option v-for="i in inboxList" :key="i.id" :value="i.id">{{ i.name }}</option>
        </select>
        <select
          v-model="filterLabel"
          class="px-2 py-1.5 text-sm bg-n-alpha-2 text-n-slate-12 rounded-lg border border-n-weak focus:outline-none focus:border-n-brand"
        >
          <option value="">{{ t('KANBAN.FILTERS.ALL_LABELS') }}</option>
          <option v-for="l in labelList" :key="l.id" :value="l.title">{{ l.title }}</option>
        </select>
        <button
          v-if="search || filterAgent || filterInbox || filterLabel"
          class="px-2 py-1.5 text-xs text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="clearFilters"
        >
          {{ t('KANBAN.FILTERS.CLEAR') }}
        </button>
      </div>

      <div class="flex items-center gap-2">
        <button
          v-tooltip="t('KANBAN.EXPORT')"
          class="p-1.5 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
          @click="exportBoard"
        >
          <Icon icon="i-lucide-download" class="size-4" />
        </button>
        <button
          v-tooltip="t('KANBAN.IMPORT')"
          class="p-1.5 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
          @click="triggerImport"
        >
          <Icon icon="i-lucide-upload" class="size-4" />
        </button>
        <input
          ref="importInputRef"
          type="file"
          accept="application/json"
          class="hidden"
          @change="onImportFile"
        />
      </div>
    </div>

    <!-- Board -->
    <div class="flex flex-1 gap-3 p-4 overflow-x-auto overflow-y-hidden scroll-smooth">
      <KanbanColumn
        v-for="col in columns"
        :key="col.id"
        :column="col"
        :conversations="conversationsByColumn[col.id] || []"
        :is-loading="isLoading"
        :inboxes="inboxes"
        @drop-card="onDropCard"
        @rename="renameColumn"
        @remove="removeColumn"
        @reorder-column="reorderColumn"
        @add-conversation="openAddPanel"
      />

      <!-- Add column control -->
      <div class="flex flex-col flex-shrink-0 w-72 h-full">
        <div v-if="!addColumnOpen">
          <button
            class="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors border border-dashed border-n-weak"
            @click="addColumnOpen = true; newColumnColor = randomColor()"
          >
            <Icon icon="i-lucide-plus" class="size-4" />
            <span>{{ t('KANBAN.ADD_COLUMN') }}</span>
          </button>
        </div>
        <div
          v-else
          class="flex flex-col gap-2 p-3 rounded-lg bg-n-solid-1 border border-n-weak"
        >
          <input
            v-model="newColumnTitle"
            type="text"
            :placeholder="t('KANBAN.NEW_COLUMN_PLACEHOLDER')"
            class="px-2 py-1.5 text-sm bg-n-alpha-2 text-n-slate-12 rounded border border-n-weak focus:outline-none focus:border-n-brand"
            @keydown.enter="createColumn"
            @keydown.esc="addColumnOpen = false"
          />
          <div class="flex items-center gap-1.5 flex-wrap">
            <button
              v-for="c in DEFAULT_COLORS"
              :key="c"
              class="size-6 rounded-full border-2 transition-transform hover:scale-110"
              :class="newColumnColor === c ? 'border-n-slate-12' : 'border-transparent'"
              :style="{ backgroundColor: c }"
              @click="newColumnColor = c"
            />
          </div>
          <div class="flex items-center gap-2 justify-end">
            <button
              class="px-2 py-1 text-xs text-n-slate-11 hover:text-n-slate-12"
              @click="addColumnOpen = false"
            >
              {{ t('KANBAN.CANCEL') }}
            </button>
            <button
              class="px-3 py-1 text-xs text-white bg-n-brand rounded hover:opacity-90"
              @click="createColumn"
            >
              {{ t('KANBAN.CREATE') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add conversation modal -->
    <div
      v-if="addPanelOpen"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeAddPanel"
    >
      <div class="bg-n-solid-1 rounded-xl border border-n-weak shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-semibold text-n-slate-12">{{ t('KANBAN.ADD_CONVERSATION') }}</h2>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="closeAddPanel"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
        <div class="p-4 border-b border-n-weak">
          <div class="relative">
            <Icon
              icon="i-lucide-search"
              class="absolute left-2.5 top-1/2 -translate-y-1/2 text-n-slate-9 size-4"
            />
            <input
              v-model="addPanelSearch"
              type="text"
              :placeholder="t('KANBAN.SEARCH_CONVERSATION')"
              class="w-full pl-8 pr-3 py-2 text-sm bg-n-alpha-2 text-n-slate-12 rounded-lg border border-n-weak focus:outline-none focus:border-n-brand"
            />
          </div>
        </div>
        <div class="flex-1 overflow-y-auto p-2">
          <div v-if="addPanelLoading" class="flex flex-col gap-2 p-2">
            <div v-for="i in 5" :key="i" class="h-14 rounded-lg bg-n-alpha-2 animate-pulse" />
          </div>
          <div
            v-else-if="filteredAddPanelResults.length === 0"
            class="flex flex-col items-center justify-center py-12 gap-2"
          >
            <Icon icon="i-lucide-search-x" class="text-n-slate-8 size-8" />
            <span class="text-sm text-n-slate-9">{{ t('KANBAN.NO_RESULTS') }}</span>
          </div>
          <button
            v-for="conv in filteredAddPanelResults"
            :key="conv.id"
            class="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-n-alpha-2 transition-colors text-left"
            @click="addConversationToColumn(conv)"
          >
            <Avatar
              :name="conv?.meta?.sender?.name || ''"
              :src="conv?.meta?.sender?.thumbnail || ''"
              :size="28"
              rounded-full
            />
            <div class="flex flex-col min-w-0 flex-1">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ conv?.meta?.sender?.name || '—' }}
              </span>
              <span class="text-xs text-n-slate-9">
                #{{ conv.display_id }} · {{ conv.status }}
              </span>
            </div>
            <Icon icon="i-lucide-plus" class="text-n-slate-10 size-4" />
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
